'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useSession, signOut } from 'next-auth/react';
import Image from 'next/image';
import { useNotificationStore } from '@/store/notificationStore';
import { useI18n } from '@/lib/i18n/context';
import { LanguageSwitcher } from './LanguageSwitcher';
import { frontPageStrings } from '@/locales/frontpage';

export function Header() {
  const router = useRouter();
  const { data: session } = useSession();
  const { t, locale } = useI18n();
  const fp = frontPageStrings[locale];
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const { unreadCount } = useNotificationStore();
  const menuRef = useRef<HTMLDivElement>(null);
  const appName = process.env.NEXT_PUBLIC_APP_NAME || 'MyApp';

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <header className="sticky top-0 glass z-50 border-b border-white/5">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-2 focus:z-[60] focus:rounded-lg focus:bg-brand-700 focus:px-4 focus:py-2 focus:text-sm focus:text-white"
      >
        {fp.skip}
      </a>
      <div className="max-w-screen-xl mx-auto px-4 py-2 flex items-center gap-2 sm:gap-4">
        {/* Logo */}
        <Link href="/" className="flex-shrink-0">
          <span className="text-xl sm:text-2xl font-display font-bold text-white tracking-tight">
            {appName}
          </span>
        </Link>

        {/* Search - desktop */}
        <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-2xl">
          <div className="relative w-full">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.header.searchPlaceholder}
              className="w-full bg-surface-800 border border-surface-700 rounded-3xl py-2.5 px-5 pl-12 text-sm text-white placeholder:text-text-muted focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500/20 transition-colors"
            />
            <svg className="absolute left-4 top-3 w-4 h-4 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </form>

        {/* Nav - desktop */}
        <nav className="hidden lg:flex items-center gap-6 text-sm font-medium text-gray-400">
          <Link href="/" className="hover:text-white transition-colors">{t.header.home}</Link>
        </nav>

        {/* Right Actions */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Mobile search toggle */}
          <button
            onClick={() => setShowMobileSearch(!showMobileSearch)}
            aria-label={fp.searchToggle}
            aria-expanded={showMobileSearch}
            className="md:hidden w-11 h-11 flex items-center justify-center rounded-xl hover:bg-surface-800 transition-colors text-text-secondary"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>

          {session ? (
            <>
              {/* Upload button */}
              <Link
                href="/upload"
                className="flex items-center gap-2 bg-brand-700 hover:bg-brand-600 text-white text-sm font-medium min-h-[44px] px-3 sm:px-4 rounded-2xl transition-colors"
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden sm:inline">{t.header.upload}</span>
              </Link>

              {/* Notifications */}
              <Link
                href="/notifications"
                className="relative w-11 h-11 flex items-center justify-center rounded-xl hover:bg-surface-800 transition-colors text-text-secondary hover:text-white"
                aria-label={t.header.notifications}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {unreadCount > 0 && (
                  <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-brand-500 rounded-full text-[10px] font-bold flex items-center justify-center text-white">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Link>

              {/* User menu */}
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  aria-label={session.user?.name || t.userMenu.settings}
                  aria-haspopup="menu"
                  aria-expanded={showUserMenu}
                  className="w-11 h-11 rounded-xl overflow-hidden ring-2 ring-transparent hover:ring-brand-500 transition-all"
                >
                  {session.user?.image ? (
                    <Image src={session.user.image} alt="Avatar" width={44} height={44} className="object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-brand flex items-center justify-center text-white font-bold text-sm">
                      {(session.user?.name || 'U')[0].toUpperCase()}
                    </div>
                  )}
                </button>

                {showUserMenu && (
                  <div className="absolute right-0 top-12 w-56 bg-surface-900 border border-surface-700 rounded-2xl shadow-2xl overflow-hidden z-50 animate-scale-in">
                    <div className="px-4 py-3 border-b border-surface-700">
                      <p className="font-medium text-sm text-white truncate">{session.user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{session.user?.email}</p>
                    </div>
                    <div className="py-2">
                      <Link href="/dashboard" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-surface-800 hover:text-white transition-colors">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                        {t.userMenu.dashboard}
                      </Link>
                      <Link href="/collections" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-surface-800 hover:text-white transition-colors">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                        {t.userMenu.collections}
                      </Link>
                      <Link href="/settings/profile" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-surface-800 hover:text-white transition-colors">
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                        {t.userMenu.settings}
                      </Link>
                    </div>
                    <div className="border-t border-surface-700 py-2">
                      <button
                        onClick={() => signOut({ callbackUrl: '/' })}
                        className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-400 hover:bg-surface-800 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                        {t.userMenu.logout}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex items-center gap-1 sm:gap-2">
              <Link href="/login" className="text-sm font-medium text-text-secondary hover:text-white whitespace-nowrap min-h-[44px] inline-flex items-center px-2 sm:px-4 rounded-xl transition-colors">
                {t.header.login}
              </Link>
              <Link href="/register" className="text-sm font-medium bg-brand-700 hover:bg-brand-600 text-white whitespace-nowrap min-h-[44px] inline-flex items-center px-3 sm:px-4 rounded-xl transition-colors">
                {t.header.register}
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile search bar */}
      {showMobileSearch && (
        <div className="md:hidden px-4 pb-3">
          <form onSubmit={handleSearch}>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.header.searchPlaceholderMobile}
              autoFocus
              className="w-full bg-surface-800 border border-surface-700 rounded-2xl py-2.5 px-4 text-sm text-white placeholder:text-text-muted focus:outline-none focus:border-brand-500"
            />
          </form>
        </div>
      )}
    </header>
  );
}